// **The demo stage's cadence policy** (`demo-presentation-shell` tasks 5.2/5.4/5.6, design
// **DD3**). Pure: no worker, no `postMessage`, no wasm, no real time — the same policy/host split
// `practice-worker.ts` keeps against `practice.worker.ts`, and for the same reason. Every rule
// below is one the projector can break (a GPU stall, a hidden tab, a fifteen-thousand-tick
// fast-forward), so each is asserted in `stage-clock.test.ts` against a `FakeClock` rather than
// discovered on stage.
//
// Three rules, one file:
//
//   * **Whole ticks only.** The world steps `floor(elapsed / msPerTick)`; the remainder is kept so
//     the cadence does not drift. A variable step derived from frame time would make the sim
//     non-deterministic frame to frame, which is the one thing the engine is not allowed to be.
//   * **Bounded catch-up, and the excess is DROPPED.** Past {@link DEMO_MAX_CATCH_UP_TICKS} the
//     backlog is discarded, never carried: a stalled projector must resume at normal speed, not
//     sprint through the stall in front of the audience, and a carried backlog compounds into a
//     spiral the world never comes out of.
//   * **A pause is a rebase, not a credit.** `resume()` clears the accumulator and rebases on the
//     current clock, so a tab hidden for ten minutes replays none of it.
//
// **A fourth, later addition: the clock-rate multiplier is not a catch-up.** Catch-up drops ticks
// a stalled projector could never really have produced; a rate is time the presenter *asks* the
// world to run faster than, in front of an audience, on purpose. It scales how much sim time a
// slice of real time is worth (`tick()` below), leaving the catch-up bound — a fixed count of
// *world* ticks — untouched at every rate: {@link DEMO_MAX_CATCH_UP_TICKS} keeps meaning "one
// second of world time" whether that world time is being earned at 1x or 4x real speed.

import type { Clock } from '../../canvas/frame-scheduler';
import { SIM_TICK_RATE_HZ } from '../../generated/sim-constants';

/**
 * The sim's fixed rate. Not a demo choice and not a literal: the demo world runs **on this
 * device**, in `waggle-wasm` compiled from the same `Balance::seed()` the mirror is generated
 * from, which is the same provenance `practice-watch.ts` reads it under
 * (client-tick-rate-provenance E4's device-side-practice class). There is no published rate to
 * prefer — C-1's configure reports the field, the sense radius and the interaction range, and
 * deliberately not a tick rate, because the module answering is the one that owns it.
 */
export const DEMO_TICK_HZ = SIM_TICK_RATE_HZ;

/**
 * The catch-up bound, in ticks: **one second of world time**.
 *
 * Small deliberately. This is the amount of stall the world will silently make up before it starts
 * dropping time, and a demo stage's failure mode is an audience watching the bees teleport. One
 * second is enough to absorb a garbage-collection pause or a slow frame and short enough that a
 * real stall reads as a pause rather than a fast-forward.
 */
export const DEMO_MAX_CATCH_UP_TICKS = DEMO_TICK_HZ;

/** How many ticks a cooperative fast-forward advances before it yields. 15 Hz × ~8 s of world
 * time: large enough that the yields are not the dominant cost of a many-thousand-tick advance,
 * small enough that a cancel is acted on well inside the ≤ 1 s failsafe latency design **DD12**
 * pins. */
export const DEMO_FAST_FORWARD_BATCH_TICKS = 128;

/** See the floor below: a rounding tolerance for an accumulator of non-terminating tick lengths. */
const FLOOR_EPSILON_MS = 1e-6;

/**
 * **The clock-rate multiplier** — a presenter lever, cycled by the `s` key (`DemoApp.tsx`) so a
 * growth beat ("If I keep applying this concept...") happens inside a sentence rather than a
 * silence. Fixed and small on purpose: a rate the presenter cannot reason about on stage is worse
 * than one they can, and {@link nextDemoClockRate}'s cycle only ever visits these three.
 */
export const DEMO_CLOCK_RATES = [1, 2, 4] as const;

export type DemoClockRate = (typeof DEMO_CLOCK_RATES)[number];

/**
 * The whole demo's default (spec: "the world runs at 2x by default"). Read by
 * {@link createStageClock} itself when a caller supplies no `rate` — which is what makes the
 * default apply **from the clock's construction**, not from the presenter's first `s` press:
 * `demo.worker.ts` builds its one clock at module load, with no `rate` in its deps, so this value
 * is already in effect before `configure` ever runs and long before any key is pressed. Nothing
 * needs to post a `setRate` at show-start for the default to hold.
 */
export const DEFAULT_DEMO_CLOCK_RATE: DemoClockRate = 2;

/** Clamps to {@link DEMO_CLOCK_RATES} rather than trusting a caller — `setRate` is reached over a
 * `postMessage` boundary (`demo-worker-protocol.ts`), and a malformed or stale message naming an
 * arbitrary number must not hand the accumulator a rate the rest of this module never validated.
 * Falls back to the default, not to the nearest member: an invalid rate is a protocol bug, and the
 * default is the one rate every part of the show is already tuned to tolerate. */
function clampRate(rate: number): DemoClockRate {
  return (DEMO_CLOCK_RATES as readonly number[]).includes(rate)
    ? (rate as DemoClockRate)
    : DEFAULT_DEMO_CLOCK_RATE;
}

/** `s`'s own cycle, 1x -> 2x -> 4x -> 1x — pure and exported so `DemoApp`'s keydown handler and
 * this module's own tests share one definition of the order, rather than each hand-rolling it. */
export function nextDemoClockRate(rate: DemoClockRate): DemoClockRate {
  const index = DEMO_CLOCK_RATES.indexOf(rate);
  return DEMO_CLOCK_RATES[(index + 1) % DEMO_CLOCK_RATES.length]!;
}

export interface StageClockDeps {
  clock: Clock;
  tickHz?: number;
  maxCatchUpTicks?: number;
  /** The starting rate. Defaults to {@link DEFAULT_DEMO_CLOCK_RATE}, clamped like every later
   * `setRate` call. */
  rate?: number;
}

export interface StageClock {
  /** Whole ticks owed since the last call. `0` while paused. */
  tick(): number;
  /** Stops owing ticks. Idempotent. */
  pause(): void;
  /** Resumes from the **current** clock with an empty accumulator, so the paused interval is not
   * replayed. Idempotent. */
  resume(): void;
  /**
   * Changes the clock-rate multiplier, clamped to {@link DEMO_CLOCK_RATES}.
   *
   * **Does not touch the accumulator.** `carriedMs` already holds *sim* time earned at whatever
   * rate was in effect when it accrued; rescaling it retroactively on a rate change would make a
   * rate change itself produce a burst or a stall of ticks, which is not what "run faster from now
   * on" means. Only time elapsed **after** this call is converted at the new rate — see `tick()`.
   *
   * **The catch-up bound moves with the rate, and it has to.** This once read "the bound is a count
   * of world ticks, so it needs no rate-dependent handling" — which was wrong about the thing the
   * bound is for. {@link DEMO_MAX_CATCH_UP_TICKS} is documented as "one second of world time",
   * chosen as "enough to absorb a garbage-collection pause or a slow frame" — and a GC pause is a
   * quantity of *real* time. Held at a fixed tick count while `tick()` multiplies elapsed real time,
   * the real stall the clock tolerates before it silently discards world time became `1 s / rate`:
   * 250 ms at 4x. That is the wrong way round, because 4x exists for chapter 3's growth beat, which
   * is the slowest-framing scene in the show. So the bound is scaled by the rate and one second of
   * real stall is absorbed at every rate.
   */
  setRate(rate: number): void;
  readonly paused: boolean;
}

export function createStageClock(deps: StageClockDeps): StageClock {
  const msPerTick = 1000 / (deps.tickHz ?? DEMO_TICK_HZ);
  const baseCatchUp = Math.max(1, deps.maxCatchUpTicks ?? DEMO_MAX_CATCH_UP_TICKS);
  let rate: DemoClockRate = clampRate(deps.rate ?? DEFAULT_DEMO_CLOCK_RATE);
  /** The bound in world ticks at the *current* rate — see `setRate`'s note. `baseCatchUp` is the
   * tolerated stall in ticks-per-1x, i.e. in real seconds; multiplying by the rate keeps the real
   * stall constant instead of dividing it. */
  const maxCatchUpTicks = (): number => baseCatchUp * rate;
  let last = deps.clock.now();
  let carriedMs = 0;
  let paused = false;

  return {
    get paused() {
      return paused;
    },
    tick(): number {
      const at = deps.clock.now();
      if (paused) {
        // Rebased every call, not only on resume: a `tick()` that ran while hidden must not leave
        // a gap behind for the next one to discover.
        last = at;
        return 0;
      }
      // The rate converts *this slice* of newly-elapsed real time into sim time — never the
      // already-accumulated `carriedMs`, which was converted at whatever rate applied when it was
      // earned (see `setRate`'s own doc).
      carriedMs += Math.max(0, at - last) * rate;
      last = at;
      // `msPerTick` is 66.666… ms and does not divide anything exactly, so an accumulator holding
      // what ought to be a whole number of ticks lands a few ulps under it and floors one tick
      // short. The epsilon is a rounding tolerance, not a fudge factor: it is a millionth of a
      // millisecond against a 66 ms tick.
      const owed = Math.floor((carriedMs + FLOOR_EPSILON_MS) / msPerTick);
      if (owed <= 0) {
        return 0;
      }
      const maxCatchUp = maxCatchUpTicks();
      if (owed > maxCatchUp) {
        // Dropped whole — including the sub-tick remainder, which belongs to time that no longer
        // exists. The bound scales with the rate so the tolerated *real* stall is the same one
        // second at 1x, 2x and 4x (see `setRate`'s note).
        carriedMs = 0;
        return maxCatchUp;
      }
      carriedMs = Math.max(0, carriedMs - owed * msPerTick);
      return owed;
    },
    pause(): void {
      paused = true;
      carriedMs = 0;
      last = deps.clock.now();
    },
    resume(): void {
      paused = false;
      carriedMs = 0;
      last = deps.clock.now();
    },
    setRate(newRate: number): void {
      rate = clampRate(newRate);
    },
  };
}

export interface FastForwardRequest {
  ticks: number;
  /** Steps a batch. Synchronous — this is the engine call. */
  step: (ticks: number) => void;
  batchTicks?: number;
  /** The yield between batches. A macrotask in production: a microtask does not let a queued
   * `message` event be delivered, which is what makes the cancel *arrive*. */
  yieldToHost?: () => Promise<void>;
  /** Checked once per batch, before the batch runs. */
  isCancelled?: () => boolean;
}

/** A macrotask. Same choice, and the same reason, as `practice-worker.ts`'s. */
function macrotask(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

/**
 * Advances `ticks` in bounded batches, yielding between them, stopping early on a cancel.
 *
 * Returns how many ticks were actually advanced — which a caller needs, because a cancelled
 * fast-forward leaves the world somewhere the cue list did not name.
 */
export async function runFastForward(request: FastForwardRequest): Promise<number> {
  const batch = Math.max(1, request.batchTicks ?? DEMO_FAST_FORWARD_BATCH_TICKS);
  const yieldToHost = request.yieldToHost ?? macrotask;
  const isCancelled = request.isCancelled ?? ((): boolean => false);
  let advanced = 0;
  while (advanced < request.ticks) {
    if (isCancelled()) {
      return advanced;
    }
    const size = Math.min(batch, request.ticks - advanced);
    request.step(size);
    advanced += size;
    if (advanced < request.ticks) {
      await yieldToHost();
    }
  }
  return advanced;
}
